import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { AppError } from 'merchant-api';
import { getCurrentAppState } from 'merchant-api/derived';
import { unifiedApiClient } from 'merchant-api/unified';
import { AppStatus } from 'merchant-api/types';

interface SearchSchema {
  statuses?: AppStatus[];
}

// Unauthenticated visitors on /application/{id} get the OTP sent to the application's email up
// front, so they land on the verify screen with the masked email instead of an email form. Any
// failure (unknown id, no stored email, network) falls back to manual email entry.
const buildOtpRedirect = async (location: { pathname: string; href: string }) => {
  const applicationId = location.pathname.match(/^\/application\/([^/]+)/)?.[1];

  if (applicationId) {
    try {
      const { data } = await unifiedApiClient.POST('/public/otp/notify', { body: { applicationId } } as never);
      const challenge = data as { key?: string; emailMasked?: string } | undefined;

      if (challenge?.key) {
        return redirect({
          to: '/login/otp',
          search: { key: challenge.key, emailMasked: challenge.emailMasked, redirect: location.href } as never,
        });
      }
    } catch {
      // fall through to plain OTP email entry
    }
  }

  return redirect({ to: '/login/otp', search: { redirect: location.href } as never });
};

export const Route = createFileRoute('/application')({
  beforeLoad: async ({ location, search }) => {
    // App-or-user guard. The unified API accepts either an app token (intake, no user yet) or a
    // user token; whoami succeeds for both. If neither credential is valid, send the visitor to OTP
    // to prove email access — not to a login-server password page.
    try {
      await unifiedApiClient.GET('/client/whoami');
    } catch (error) {
      if (error instanceof AppError && error.name === 'Unauthenticated') {
        throw await buildOtpRedirect(location);
      }
      throw error;
    }

    // Resume: from the bare /application route, route the user to their current application's state.
    // Association is handled server-side by the single submit (fired by AppReview), so there is no associate redirect here.
    if (location.pathname !== '/application') return;

    const result = await getCurrentAppState({ statuses: search.statuses });
    if (!result) throw redirect({ to: '/apply/landing' });

    throw redirect({ to: `/application/$id/${result.state}`, params: { id: result.id } });
  },
  validateSearch: (search: Record<string, unknown>): SearchSchema => {
    return {
      statuses: search.statuses as AppStatus[],
    };
  },
  component: Outlet,
});
